/**
 * Keuangan dropdown — dipakai di header dashboard (dan TopNavbar jika dipakai lagi).
 */

import * as React from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Wallet, CreditCard, FileText, List, History, Send, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  canAccessKeuanganRekening,
  canAccessKeuanganTransferPlans,
  hasAnyPrivilege,
} from "@/utils/privileges";
import type { AdminPrivilege } from "@/services/adminService";
import { cn } from "@/lib/utils";

interface KeuanganNavDropdownProps {
  /** Layout mobile sheet: tombol full width */
  mobile?: boolean;
  className?: string;
}

export function KeuanganNavDropdown({ mobile, className }: KeuanganNavDropdownProps) {
  const [location] = useLocation();
  const { user } = useAuth();

  const canAccess = (privileges: AdminPrivilege[]) =>
    privileges.length === 0 || hasAnyPrivilege(user, privileges);

  /** Access is privilege-based (no special casing for admin stage 1/2). */
  const showKeuanganRekening = canAccessKeuanganRekening(user);
  const showKeuanganBuatRencana = canAccess(["create_transaction_plan", "manage_payments"]);
  const showKeuanganRiwayatTransfer = canAccess([
    "manage_historical_transfer",
    "manage_payments",
  ]);
  const showKeuanganDaftarRencana = canAccessKeuanganTransferPlans(user);
  const showKeuanganRiwayatTransaksi = canAccess([
    "manage_transactions",
    "manage_transactions_plan",
    "view_reports",
  ]);
  const showKeuanganMenu =
    showKeuanganRekening ||
    showKeuanganBuatRencana ||
    showKeuanganDaftarRencana ||
    showKeuanganRiwayatTransaksi ||
    showKeuanganRiwayatTransfer;

  if (!showKeuanganMenu) return null;

  const active =
    location.startsWith("/keuangan") || location === "/historical-transaction";

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={active ? "secondary" : "ghost"}
            size="sm"
            title="Keuangan"
            className={cn(
              "gap-2",
              mobile && "w-full justify-start",
              !mobile && "max-sm:gap-1 max-sm:px-2"
            )}
          >
            <Wallet className="h-4 w-4 shrink-0" />
            <span className={cn(!mobile && "max-sm:sr-only")}>Keuangan</span>
            <ChevronDown
              className={cn(
                "h-3 w-3 shrink-0",
                mobile ? "ml-1" : "ml-1 max-sm:ml-0 max-sm:sr-only"
              )}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[min(70vh,calc(100dvh-4rem))] overflow-y-auto">
          {showKeuanganRekening && (
            <DropdownMenuItem asChild>
              <Link href="/keuangan/rekening" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                <span>Rekening</span>
              </Link>
            </DropdownMenuItem>
          )}
          {(showKeuanganBuatRencana || showKeuanganDaftarRencana) && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Rencana Transfer</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {showKeuanganBuatRencana && (
                  <DropdownMenuItem asChild>
                    <Link href="/keuangan/transfer-plan" className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span>Buat Rencana Transfer</span>
                    </Link>
                  </DropdownMenuItem>
                )}
                {showKeuanganDaftarRencana && (
                  <DropdownMenuItem asChild>
                    <Link href="/keuangan/transfer-plans" className="flex items-center gap-2">
                      <List className="h-4 w-4" />
                      <span>Daftar Rencana Transfer</span>
                    </Link>
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
          {showKeuanganRiwayatTransaksi && (
            <DropdownMenuItem asChild>
              <Link href="/historical-transaction" className="flex items-center gap-2">
                <History className="h-4 w-4" />
                <span>Riwayat Transaksi</span>
              </Link>
            </DropdownMenuItem>
          )}
          {showKeuanganRiwayatTransfer && (
            <DropdownMenuItem asChild>
              <Link href="/keuangan/riwayat-transfer" className="flex items-center gap-2">
                <Send className="h-4 w-4" />
                <span>Riwayat Transfer</span>
              </Link>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
